import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Drawer,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Checkbox,
  Row,
  Col,
  Tooltip,
  Select,
  TimePicker,
  InputNumber,
  Segmented,
  Spin,
  DatePicker,
  Divider,
  Upload,
  Progress,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  TranslationOutlined,
  CopyOutlined,
  MinusCircleOutlined,
  CarOutlined,
  SaveOutlined,
  ShoppingOutlined,
  NotificationOutlined,
  CalendarOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  RocketOutlined,
  SyncOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import AuthService from '../utils/auth';
import RichTextEditor from '../components/RichTextEditor';
import ProductImageUpload from '../components/ProductImageUpload';
import ProductDocumentManager from '../components/ProductDocumentManager';
import ProductDayEditor from '../components/ProductDayEditor';

const { TextArea } = Input;
const { Option } = Select;

const DEFAULT_PICKUP_POINTS = [
  { name: 'Northcote 大华对面', time: null },
  { name: '市区 9 Wolfe Street', time: null },
  { name: 'Greenlane 麦当劳', time: null },
];

const normalizePickupPoints = (points) => {
  if (!points) return [];
  return points.map(p => typeof p === 'string' ? { name: p, time: '' } : p);
};

const normalizePickupPointsForForm = (points) => {
  if (!points) return [];
  return points.map(p => {
    if (typeof p === 'string') return { name: p, time: null };
    return { ...p, time: p.time ? dayjs(p.time, 'HH:mm') : null };
  });
};

const DestinationManagement = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);

  // 页面刷新后自动恢复编辑状态
  const savedEditId = React.useRef(localStorage.getItem('destEditingId'));
  const restoredRef = React.useRef(false);
  const [allAttachments, setAllAttachments] = useState([]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [translating, setTranslating] = useState({});
  const [activeCategory, setActiveCategory] = useState('standard');
  const [searchText, setSearchText] = useState('');
  const [listScope, setListScope] = useState('active'); // active=有未来团 | all=全部
  const [syncing, setSyncing] = useState(false);
  const [form] = Form.useForm();

  // 产品资料相关状态
  const EDITOR_MODES = ['basic', 'product', 'itinerary', 'notice'];
  const [editorMode, setEditorMode] = useState(() => {
    const saved = localStorage.getItem('destEditorMode');
    return EDITOR_MODES.includes(saved) ? saved : 'basic';
  });
  const handleSetEditorMode = (mode) => {
    setEditorMode(mode);
    localStorage.setItem('destEditorMode', mode);
  };
  const [productForm] = Form.useForm();
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productSaving, setProductSaving] = useState(false);

  // 产品文档（单文档）
  const [productDoc, setProductDoc] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const aiTimerRef = React.useRef(null);

  // 分类与产品名对不上时的提示，由后端保存接口回传（规则只在后端一份，见 utils/tourCategoryRule）。
  // 必须声明在 triggerAutoSave 之前 —— 那个 useCallback 里会调 setCategoryHint。
  const [categoryHint, setCategoryHint] = useState(null);

  // 自动保存
  const autoSaveTimerRef = React.useRef(null);
  const autoSaveProductTimerRef = React.useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'

  const triggerAutoSave = React.useCallback((saveType) => {
    const timerRef = saveType === 'destination' ? autoSaveTimerRef : autoSaveProductTimerRef;
    if (timerRef.current) clearTimeout(timerRef.current);
    setAutoSaveStatus(null);
    timerRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        if (saveType === 'destination' && editingDestination) {
          const values = form.getFieldsValue();
          const data = { ...values, attachmentIds: selectedAttachmentIds };
          if (data.pickupPoints) {
            data.pickupPoints = data.pickupPoints.map(p => ({
              name: p.name,
              time: p.time ? (typeof p.time === 'string' ? p.time : p.time.format('HH:mm')) : ''
            }));
          }
          const saved = await axios.put(`${API_BASE}/destinations/${editingDestination.id}`, data, {
            headers: AuthService.getAuthHeaders()
          });
          setCategoryHint(saved.data?.categoryHint || null);
        } else if (saveType === 'product') {
          const values = productForm.getFieldsValue();
          if (!values.title && !values.price) return; // 空表单不保存
          if (!values.destination) values.destination = editingDestination?.name;
          if (productData?.id) {
            await axios.put(`${API_BASE}/travel-products/${productData.id}`, values, {
              headers: AuthService.getAuthHeaders()
            });
          } else if (editingDestination?.id) {
            values.destinationId = editingDestination.id;
            const res = await axios.post(`${API_BASE}/travel-products`, values, {
              headers: AuthService.getAuthHeaders()
            });
            if (res.data.success) setProductData(res.data.data);
          }
        }
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 2000);
      } catch (err) {
        setAutoSaveStatus(null);
      }
    }, 2000);
  }, [editingDestination, productData, selectedAttachmentIds]);

  // 快捷出团日期
  const [destTours, setDestTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [addingTour, setAddingTour] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchDestinations();
    fetchAttachments();
  }, []);

  // 刷新后自动恢复到正在编辑的产品
  useEffect(() => {
    if (!restoredRef.current && savedEditId.current && destinations.length > 0) {
      restoredRef.current = true;
      const record = destinations.find(d => d.id === parseInt(savedEditId.current));
      if (record) {
        handleEdit(record);
      }
    }
  }, [destinations]);

  const fetchDestinations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/destinations?source=all`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setDestinations(response.data.data);
      }
    } catch (error) {
      message.error('获取产品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTtourSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_BASE}/ttour/sync`, {}, { headers: AuthService.getAuthHeaders() });
      const d = res.data.data;
      message.success(
        `同步完成：新增产品 ${d.productsCreated}，新增出团 ${d.toursCreated}，跳过产品 ${d.productsSkipped}，跳过出团 ${d.toursSkipped}${d.errors.length ? `，${d.errors.length} 个错误` : ''}`
      );
      if (d.errors.length) console.warn('同步错误:', d.errors);
      fetchDestinations();
    } catch (err) {
      message.error(err.response?.data?.error || '同步失败');
    } finally {
      setSyncing(false);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await axios.get(`${API_BASE}/notice-attachments?isActive=true`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setAllAttachments(response.data.data);
      }
    } catch (error) {
      console.error('获取附件列表失败:', error);
    }
  };

  // 翻译中文字段到英文
  const handleTranslate = async (chineseField, englishField) => {
    const chineseText = form.getFieldValue(chineseField);
    if (!chineseText || chineseText.trim() === '') {
      message.warning('请先填写中文内容');
      return;
    }

    setTranslating(prev => ({ ...prev, [chineseField]: true }));
    try {
      const response = await axios.post(`${API_BASE}/translate`, {
        text: chineseText,
        context: '旅游产品相关信息'
      }, {
        headers: AuthService.getAuthHeaders()
      });

      if (response.data.success && response.data.data.translation) {
        form.setFieldsValue({ [englishField]: response.data.data.translation });
        message.success('翻译完成');
      }
    } catch (error) {
      console.error('翻译失败:', error);
      message.error('翻译失败，请稍后重试');
    } finally {
      setTranslating(prev => ({ ...prev, [chineseField]: false }));
    }
  };

  // 删除出团
  const handleDeleteTour = async (tourId, tourName) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定删除出团 ${tourName} 吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await axios.delete(`${API_BASE}/tours/${tourId}`, {
            headers: AuthService.getAuthHeaders()
          });
          message.success('出团已删除');
          fetchToursForDestination(editingDestination.id);
        } catch (err) {
          message.error(err.response?.data?.error || '删除失败，可能已有游客报名');
        }
      }
    });
  };

  // 获取该产品的出团日期列表
  const fetchToursForDestination = async (destinationId) => {
    setToursLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tours`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        const filtered = res.data.data.filter(t => t.destinationId === destinationId);
        setDestTours(filtered.sort((a, b) => new Date(b.departureDate) - new Date(a.departureDate)));
      }
    } catch (err) {
      console.error('获取出团列表失败:', err);
    }
    setToursLoading(false);
  };

  // 快捷添加出团日期
  const handleQuickAddTour = async (date) => {
    if (!editingDestination?.id || !date) return;
    setAddingTour(true);
    try {
      const res = await axios.post(`${API_BASE}/tours`, {
        destinationId: editingDestination.id,
        departureDate: date.format('YYYY-MM-DD'),
        tourType: editingDestination.category === 'daytour' ? 'daytour' : 'standard'
      }, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        message.success(`出团 ${res.data.data.productName} 已创建`);
        fetchToursForDestination(editingDestination.id);
      }
    } catch (err) {
      message.error(err.response?.data?.error || '创建出团失败');
    }
    setAddingTour(false);
  };

  // 所有产品资料列表（用于复制）
  const [allProducts, setAllProducts] = useState([]);
  const fetchAllProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/travel-products?pageSize=100`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) setAllProducts(res.data.data);
    } catch (err) { /* ignore */ }
  };

  // 从其他产品复制产品资料
  const handleCopyProduct = async (sourceId) => {
    if (!sourceId) return;
    try {
      const res = await axios.get(`${API_BASE}/travel-products/${sourceId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        const source = res.data.data;
        // 复制内容字段，但不复制标题、目的地等基本信息
        const copyFields = [
          'subtitle', 'duration', 'departure', 'departureSchedule',
          'price', 'originalPrice', 'depositAmount', 'paymentType',
          'pricingDetails', 'highlights', 'tags', 'category', 'flag',
          'description', 'itinerary', 'features',
          'costInclusion', 'costExclusion', 'mealArrangement', 'optionalActivities',
          'transferInfo', 'hotelPolicy', 'roomSharingPolicy',
          'specialNotes', 'cancellationPolicy', 'conditions', 'insuranceInfo',
          'playNotices', 'importantTips'
        ];
        const values = {};
        copyFields.forEach(f => { if (source[f] !== null && source[f] !== undefined) values[f] = source[f]; });
        productForm.setFieldsValue(values);
        message.success(`已从「${source.title || source.destination}」复制产品资料`);
      }
    } catch (err) {
      message.error('复制失败');
    }
  };

  // 产品文档：获取/上传/下载
  const fetchProductDoc = async (destId) => {
    try {
      const res = await axios.get(`${API_BASE}/product-documents/destination/${destId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success && res.data.data.length > 0) {
        setProductDoc(res.data.data[0]);
      } else {
        setProductDoc(null);
      }
    } catch (err) { setProductDoc(null); }
  };

  const handleUploadDoc = async (options) => {
    const { file, onSuccess, onError } = options;
    setDocUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('destinationId', editingDestination?.id);
    try {
      const res = await axios.post(`${API_BASE}/product-documents/upload`, formData, {
        headers: { ...AuthService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        message.success('文档上传成功');
        setProductDoc(res.data.data);
        onSuccess(res.data);
      } else {
        message.error(res.data.message || '上传失败');
        onError(new Error(res.data.message));
      }
    } catch (err) {
      message.error('上传失败');
      onError(err);
    }
    setDocUploading(false);
  };

  const handleDownloadDoc = async () => {
    if (!productDoc) return;
    try {
      const res = await axios.get(`${API_BASE}/product-documents/${productDoc.id}/download-url`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        let url = res.data.data.url;
        if (url.startsWith('/uploads/')) url = API_BASE.replace('/api', '') + url;
        window.open(url, '_blank');
      }
    } catch (err) { message.error('下载失败'); }
  };

  const handleDeleteDoc = async () => {
    if (!productDoc) return;
    try {
      await axios.delete(`${API_BASE}/product-documents/${productDoc.id}`, {
        headers: AuthService.getAuthHeaders()
      });
      setProductDoc(null);
      message.success('文档已删除');
    } catch (err) { message.error('删除失败'); }
  };

  // AI 提取产品资料
  const handleAiExtract = async () => {
    if (!productDoc || productDoc.id === 'skip') return;
    setAiExtracting(true);
    setAiProgress(0);
    const startMs = Date.now();
    aiTimerRef.current = setInterval(() => {
      const elapsedSec = (Date.now() - startMs) / 1000;
      // 根据实际耗时估算进度：基本信息约90s(40%)，行程约按天数递增
      let pct;
      if (elapsedSec < 90) pct = (elapsedSec / 90) * 40;
      else pct = 40 + Math.min(55, (elapsedSec - 90) / 4);
      setAiProgress(Math.min(95, Math.round(pct)));
    }, 1000);
    try {
      const res = await axios.post(`${API_BASE}/ai/extract`, {
        documentId: productDoc.id,
        destinationId: editingDestination?.id
      }, { headers: AuthService.getAuthHeaders(), timeout: 600000 });

      if (res.data.success) {
        const data = res.data.data;
        // 填充产品表单
        const formFields = { ...data };
        delete formFields.days;
        productForm.setFieldsValue(formFields);

        // 重新加载产品数据
        fetchProductForDestination(editingDestination.id, editingDestination.name);

        setAiProgress(100);
        message.success(`AI 提取完成！${res.data.meta.elapsed}s，$${res.data.meta.cost}`);
      } else {
        message.error(res.data.message || 'AI 提取失败');
      }
    } catch (err) {
      message.error('AI 提取失败: ' + (err.response?.data?.message || err.message));
    }
    clearInterval(aiTimerRef.current);
    setAiExtracting(false);
    setAiProgress(0);
  };

  // 获取关联的产品资料
  const fetchProductForDestination = async (destinationId, destinationName) => {
    setProductLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/travel-products?destinationId=${destinationId}&pageSize=1`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success && res.data.data.length > 0) {
        const product = res.data.data[0];
        // 获取完整详情
        const detailRes = await axios.get(`${API_BASE}/travel-products/${product.id}`, {
          headers: AuthService.getAuthHeaders()
        });
        if (detailRes.data.success) {
          setProductData(detailRes.data.data);
          productForm.setFieldsValue(detailRes.data.data);
        }
      } else {
        // 没有关联产品，预填目的地信息
        setProductData(null);
        productForm.resetFields();
        productForm.setFieldsValue({
          title: destinationName,
          destination: destinationName,
          destinationId: destinationId,
          status: 0,
          price: 0
        });
      }
    } catch (err) {
      console.error('获取产品资料失败:', err);
    }
    setProductLoading(false);
  };

  // 保存产品资料
  const handleSaveProduct = async () => {
    try {
      const values = await productForm.validateFields();
      setProductSaving(true);

      if (productData?.id) {
        await axios.put(`${API_BASE}/travel-products/${productData.id}`, values, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('产品资料已更新');
      } else {
        const res = await axios.post(`${API_BASE}/travel-products`, {
          ...values,
          destinationId: editingDestination?.id,
          destination: values.destination || editingDestination?.name
        }, {
          headers: AuthService.getAuthHeaders()
        });
        if (res.data.success) {
          setProductData(res.data.data);
          message.success('产品资料已创建');
        }
      }
    } catch (err) {
      if (err.errorFields) {
        message.error('请检查必填字段');
        return;
      }
      message.error('保存失败: ' + (err.response?.data?.message || err.message));
    }
    setProductSaving(false);
  };

  // 发布/下架产品
  const handleToggleProductStatus = async () => {
    if (!productData?.id) return;
    try {
      const res = await axios.patch(`${API_BASE}/travel-products/${productData.id}/toggle-status`, {}, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        const newStatus = res.data.data.status;
        setProductData(prev => ({ ...prev, status: newStatus }));
        productForm.setFieldsValue({ status: newStatus });
        message.success(res.data.message);
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleCopyFrom = (sourceId) => {
    const source = destinations.find(d => d.id === sourceId);
    if (!source) return;
    form.setFieldsValue({
      departureGuide: source.departureGuide,
      departureGuideEn: source.departureGuideEn,
      entryGuide: source.entryGuide,
      entryGuideEn: source.entryGuideEn,
      customsInfo: source.customsInfo,
      customsInfoEn: source.customsInfoEn,
      vaccineInfo: source.vaccineInfo,
      vaccineInfoEn: source.vaccineInfoEn,
      localNotices: source.localNotices,
      localNoticesEn: source.localNoticesEn,
      insuranceInfo: source.insuranceInfo,
      insuranceInfoEn: source.insuranceInfoEn,
      paymentInfo: source.paymentInfo,
      paymentInfoEn: source.paymentInfoEn,
      weatherInfo: source.weatherInfo,
      weatherInfoEn: source.weatherInfoEn,
      gatheringInfo: source.gatheringInfo,
      gatheringInfoEn: source.gatheringInfoEn,
      domesticTransportInfo: source.domesticTransportInfo,
      domesticTransportInfoEn: source.domesticTransportInfoEn,
      scheduleRef: source.scheduleRef,
      scheduleRefEn: source.scheduleRefEn,
      mealArrangement: source.mealArrangement,
      mealArrangementEn: source.mealArrangementEn,
      consumptionInfo: source.consumptionInfo,
      consumptionInfoEn: source.consumptionInfoEn,
      costInclusion: source.costInclusion,
      costInclusionEn: source.costInclusionEn,
      playNotices: source.playNotices,
      playNoticesEn: source.playNoticesEn,
      importantTips: source.importantTips,
      importantTipsEn: source.importantTipsEn,
      purchaseTerms: source.purchaseTerms,
      purchaseTermsEn: source.purchaseTermsEn,
    });
    setSelectedAttachmentIds(source.attachmentIds || []);
    message.success(`已从「${source.name}」复制内容`);
  };

  const handleAdd = async () => {
    try {
      // 自动创建一个空的 Destination
      const res = await axios.post(`${API_BASE}/destinations`, {
        name: `新产品_${Date.now()}`,
        category: activeCategory,
        isActive: true
      }, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        const newDest = res.data.data;
        setCategoryHint(null);
        setEditingDestination(newDest);
        localStorage.setItem('destEditingId', newDest.id);
        setSelectedAttachmentIds([]);
        setProductData(null);
        setDestTours([]);
        productForm.resetFields();
        fetchProductDoc(newDest.id);
        fetchAllProducts();
        form.resetFields();
        form.setFieldsValue({
          name: '',
          category: activeCategory,
          pickupPoints: DEFAULT_PICKUP_POINTS.map(p => ({ ...p }))
        });
        setModalVisible(true);
        fetchDestinations();
      }
    } catch (err) {
      message.error(err.response?.data?.message || err.response?.data?.error || '创建产品失败');
    }
  };

  const handleEdit = (record) => {
    setCategoryHint(null); // 上一个产品的提示不能留到下一个产品
    setEditingDestination(record);
    localStorage.setItem('destEditingId', record.id);
    setProductData(null);
    setDestTours([]);
    productForm.resetFields();
    setSelectedAttachmentIds(record.attachmentIds || []);
    fetchProductForDestination(record.id, record.name);
    fetchToursForDestination(record.id);
    fetchAllProducts();
    fetchProductDoc(record.id);
    form.setFieldsValue({
      name: record.name,
      productCode: record.productCode || undefined,
      category: record.category || 'standard',
      adultPrice: record.adultPrice ? parseFloat(record.adultPrice) : undefined,
      seniorPrice: record.seniorPrice ? parseFloat(record.seniorPrice) : undefined,
      adultCommission: record.adultCommission ? parseFloat(record.adultCommission) : undefined,
      seniorCommission: record.seniorCommission ? parseFloat(record.seniorCommission) : undefined,
      entryGuide: record.entryGuide,
      entryGuideEn: record.entryGuideEn,
      customsInfo: record.customsInfo,
      customsInfoEn: record.customsInfoEn,
      vaccineInfo: record.vaccineInfo,
      vaccineInfoEn: record.vaccineInfoEn,
      localNotices: record.localNotices,
      localNoticesEn: record.localNoticesEn,
      insuranceInfo: record.insuranceInfo,
      insuranceInfoEn: record.insuranceInfoEn,
      paymentInfo: record.paymentInfo,
      paymentInfoEn: record.paymentInfoEn,
      weatherInfo: record.weatherInfo,
      weatherInfoEn: record.weatherInfoEn,
      gatheringInfo: record.gatheringInfo,
      gatheringInfoEn: record.gatheringInfoEn,
      domesticTransportInfo: record.domesticTransportInfo,
      domesticTransportInfoEn: record.domesticTransportInfoEn,
      scheduleRef: record.scheduleRef,
      scheduleRefEn: record.scheduleRefEn,
      mealArrangement: record.mealArrangement,
      mealArrangementEn: record.mealArrangementEn,
      consumptionInfo: record.consumptionInfo,
      consumptionInfoEn: record.consumptionInfoEn,
      costInclusion: record.costInclusion,
      costInclusionEn: record.costInclusionEn,
      playNotices: record.playNotices,
      playNoticesEn: record.playNoticesEn,
      importantTips: record.importantTips,
      importantTipsEn: record.importantTipsEn,
      purchaseTerms: record.purchaseTerms,
      purchaseTermsEn: record.purchaseTermsEn,
      isActive: record.isActive,
      requiresChinaDoc: !!record.requiresChinaDoc,
      pickupPoints: record.pickupPoints && record.pickupPoints.length > 0
        ? normalizePickupPointsForForm(record.pickupPoints)
        : DEFAULT_PICKUP_POINTS.map(p => ({ ...p }))
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/destinations/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        message.success('删除成功');
        fetchDestinations();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleSave = async ({ keepOpen = false } = {}) => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        attachmentIds: selectedAttachmentIds
      };
      if (data.pickupPoints) {
        data.pickupPoints = data.pickupPoints.map(p => ({
          name: p.name,
          time: p.time ? (typeof p.time === 'string' ? p.time : p.time.format('HH:mm')) : ''
        }));
      }

      if (editingDestination) {
        await axios.put(`${API_BASE}/destinations/${editingDestination.id}`, data, {
          headers: AuthService.getAuthHeaders()
        });
        message.success(keepOpen ? '草稿已保存' : '更新成功');
      } else {
        await axios.post(`${API_BASE}/destinations`, data, {
          headers: AuthService.getAuthHeaders()
        });
        message.success(keepOpen ? '草稿已保存' : '创建成功');
      }

      if (!keepOpen) setModalVisible(false);
      fetchDestinations();
    } catch (error) {
      if (error.errorFields) {
        message.error('请检查必填字段');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('保存失败');
      }
    }
  };

  const handleSaveDraft = async () => {
    if (editorMode === 'product') {
      await handleSaveProduct();
    } else {
      await handleSave({ keepOpen: true });
    }
  };

  const closeDrawer = () => {
    setModalVisible(false);
    localStorage.removeItem('destEditingId');
  };

  const handleSaveAndClose = async () => {
    if (editorMode === 'product') {
      await handleSaveProduct();
      closeDrawer();
      fetchDestinations();
    } else {
      await handleSave();
    }
  };

  const columns = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space size={6}>
          <GlobalOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
          {record.source === 'ttour-pdf' && (
            <Tag color="blue" style={{ marginLeft: 4, fontSize: 11 }}>Ttour 同步</Tag>
          )}
        </Space>
      )
    },
    {
      title: '未来团',
      dataIndex: 'futureTourCount',
      key: 'futureTourCount',
      width: 80,
      sorter: (a, b) => (a.futureTourCount || 0) - (b.futureTourCount || 0),
      render: (n) => n ? <span style={{ fontWeight: 500, color: '#1677FF' }}>{n}</span> : <span style={{ color: '#bbb' }}>0</span>
    },
    {
      title: '最近出团',
      dataIndex: 'nextDepartureDate',
      key: 'nextDepartureDate',
      width: 110,
      sorter: (a, b) => (a.nextDepartureDate || '9999').localeCompare(b.nextDepartureDate || '9999'),
      render: (d) => d || <span style={{ color: '#bbb' }}>-</span>
    },
    {
      title: 'TTOUR 团名',
      dataIndex: 'ttourNames',
      key: 'ttourNames',
      width: 260,
      render: (names) => {
        if (!names || names.length === 0) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ minWidth: 0 }}>
            {names.map((t) => (
              <div key={t.name} style={{ wordBreak: 'break-word', color: t.differs ? '#d46b08' : '#666', fontSize: 13 }}>
                {t.name}
              </div>
            ))}
            {names.some(t => t.differs) && (
              <Tag color="orange" style={{ marginTop: 4, fontSize: 11 }}>名称不一致</Tag>
            )}
          </div>
        );
      }
    },
    {
      title: '酒店数',
      dataIndex: 'hotelCount',
      key: 'hotelCount',
      width: 80,
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: '航班批次',
      dataIndex: 'flightBatchCount',
      key: 'flightBatchCount',
      width: 90,
      render: (count) => <Tag color="green">{count}</Tag>
    },
    {
      title: 'Add-ons',
      dataIndex: 'addOnProductCount',
      key: 'addOnProductCount',
      width: 80,
      render: (count) => <Tag color="orange">{count}</Tag>
    },
    {
      // 去中国的团要客人补传身份证/台胞证；机加酒产品应关闭
      title: '中国证件',
      dataIndex: 'requiresChinaDoc',
      key: 'requiresChinaDoc',
      width: 90,
      render: (v, record) => (
        record.category === 'standard'
          ? <Tag color={v ? 'red' : 'default'}>{v ? '需要' : '不需要'}</Tag>
          : <span style={{ color: '#bbb' }}>-</span>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除该产品吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 产品名称 / 编号 模糊搜索
  const matchSearch = (d) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase().trim();
    return (d.name || '').toLowerCase().includes(q) ||
           (d.nameEn || '').toLowerCase().includes(q) ||
           (d.productCode || '').toLowerCase().includes(q);
  };
  const matchScope = (d) => listScope === 'all' || d.futureTourCount > 0;
  const visible = destinations.filter(d => matchScope(d) && matchSearch(d));
  const standardDestinations = visible.filter(d => d.category !== 'daytour' && d.category !== 'inbound');
  const daytourDestinations = visible.filter(d => d.category === 'daytour');
  const inboundDestinations = visible.filter(d => d.category === 'inbound');

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="产品管理"
        extra={
          <Space>
            <Segmented
              value={listScope}
              onChange={setListScope}
              options={[
                { label: '有未来团', value: 'active' },
                { label: '全部', value: 'all' },
              ]}
            />
            <Input.Search
              placeholder="搜索产品名称"
              allowClear
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新建产品
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeCategory}
          onChange={setActiveCategory}
          items={[
            {
              key: 'standard',
              label: `出境游 (${standardDestinations.length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={standardDestinations}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 600 }}
                  size="small"
                />
              )
            },
            {
              key: 'daytour',
              label: `一日游 (${daytourDestinations.length})`,
              children: (
                <Table
                  columns={[
                    ...columns.filter(c => !['hotelCount', 'flightBatchCount', 'addOnProductCount', 'isActive', 'requiresChinaDoc', 'ttourNames'].includes(c.dataIndex) && c.key !== 'action'),
                    {
                      title: '上车点',
                      dataIndex: 'pickupPoints',
                      key: 'pickupPoints',
                      render: (points) => {
                        const normalized = normalizePickupPoints(points);
                        if (normalized.length === 0) return <span style={{ color: '#999' }}>-</span>;
                        return (
                          <Space size={[0, 4]} wrap>
                            {normalized.map((p, i) => <Tag key={i} color="cyan"><CarOutlined /> {p.name}{p.time ? ` (${p.time})` : ''}</Tag>)}
                          </Space>
                        );
                      }
                    },
                    {
                      title: '正常价格',
                      dataIndex: 'adultPrice',
                      key: 'adultPrice',
                      width: 100,
                      render: (val) => val ? `$${parseFloat(val).toFixed(2)}` : <span style={{ color: '#999' }}>-</span>
                    },
                    {
                      title: '优惠价格',
                      dataIndex: 'seniorPrice',
                      key: 'seniorPrice',
                      width: 100,
                      render: (val) => val ? `$${parseFloat(val).toFixed(2)}` : <span style={{ color: '#999' }}>-</span>
                    },
                    columns.find(c => c.dataIndex === 'isActive'),
                    columns.find(c => c.key === 'action')
                  ].filter(Boolean)}
                  dataSource={daytourDestinations}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 600 }}
                  size="small"
                />
              )
            },
            {
              key: 'inbound',
              label: `入境游 (${inboundDestinations.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      icon={<SyncOutlined spin={syncing} />}
                      loading={syncing}
                      onClick={handleTtourSync}
                    >
                      从 Ttour 同步
                    </Button>
                  </div>
                  <Table
                    columns={columns}
                    dataSource={inboundDestinations}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 600 }}
                    size="small"
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      <Drawer
        title={editingDestination ? `编辑产品 — ${editingDestination.name}` : '新建产品'}
        open={modalVisible}
        onClose={() => { setModalVisible(false); localStorage.removeItem('destEditingId'); }}
        width="100%"
        styles={{ body: { padding: '16px 24px' } }}
        extra={
          <Space>
            {autoSaveStatus === 'saving' && <span style={{ color: '#999', fontSize: 13 }}>保存中...</span>}
            {autoSaveStatus === 'saved' && <span style={{ color: '#52c41a', fontSize: 13 }}>已自动保存</span>}
            {editorMode === 'product' && productData?.id && (
              <Button onClick={handleToggleProductStatus}>
                {productData.status === 1 ? '下架' : '发布到会员端'}
              </Button>
            )}
            {editorMode !== 'itinerary' && (
              <Button onClick={handleSaveDraft} loading={productSaving}>
                保存草稿
              </Button>
            )}
            {editorMode !== 'itinerary' && (
              <Button type="primary" onClick={handleSaveAndClose} loading={productSaving}>
                保存
              </Button>
            )}
          </Space>
        }
      >
        {/* 顶部分区切换 */}
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
          <Segmented
            value={editorMode}
            onChange={handleSetEditorMode}
            options={[
              { label: <span><InfoCircleOutlined /> 基本信息</span>, value: 'basic' },
              { label: <span><ShoppingOutlined /> 产品资料{productData?.status === 1 ? ' ✓' : ''}</span>, value: 'product' },
              { label: <span><CalendarOutlined /> 行程</span>, value: 'itinerary' },
              { label: <span><NotificationOutlined /> 出团通知</span>, value: 'notice' },
            ]}
            size="large"
          />
          {editorMode === 'product' && productData?.id && (
            <Tag color={productData.status === 1 ? 'green' : 'default'}>
              {productData.status === 1 ? '已发布' : '草稿'}
            </Tag>
          )}
        </div>

        {/* ===== 基本信息 + 出团通知（同一个产品表单，两块都常驻挂载，切换只控制显示） ===== */}
        <div style={{ display: editorMode === 'basic' || editorMode === 'notice' ? 'block' : 'none' }}>
          <Form form={form} layout="vertical" onValuesChange={() => editingDestination && triggerAutoSave('destination')}>
            {editorMode === 'notice' && destinations.filter(d => !editingDestination || d.id !== editingDestination.id).length > 0 && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f6f8fa', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CopyOutlined style={{ color: '#1890ff' }} />
                <span style={{ color: '#666', whiteSpace: 'nowrap' }}>从其他产品复制通知内容：</span>
                <Select
                  placeholder="选择产品"
                  style={{ flex: 1 }}
                  onChange={handleCopyFrom}
                  value={null}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {destinations
                    .filter(d => !editingDestination || d.id !== editingDestination.id)
                    .map(d => (
                      <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                </Select>
              </div>
            )}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.category !== currentValues.category}
            >
              {({ getFieldValue }) => {
                const category = getFieldValue('category') || 'standard';
                const bilingualTab = (key, label, cnField, cnLabel, cnPlaceholder, enField, enLabel, enPlaceholder) => ({
                  key,
                  label,
                  children: (
                    <Row gutter={16}>
                      <Col span={11}>
                        <Form.Item name={cnField} label={cnLabel}>
                          <TextArea rows={8} placeholder={cnPlaceholder} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Tooltip title="翻译到英文">
                          <Button
                            type="primary"
                            size="small"
                            icon={<TranslationOutlined />}
                            loading={translating[cnField]}
                            onClick={() => handleTranslate(cnField, enField)}
                          />
                        </Tooltip>
                      </Col>
                      <Col span={11}>
                        <Form.Item name={enField} label={enLabel}>
                          <TextArea rows={8} placeholder={enPlaceholder} />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                });

                const basicTab = {
                  key: 'basic',
                  label: '基本信息',
                  children: (
                    <>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="name" label="产品名称" rules={[{ required: true, message: '请输入产品名称' }]}>
                            <Input placeholder="如：阿根廷、日本、北京" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item name="category" label="分类" initialValue="standard">
                            <Select>
                              <Option value="standard">出境游</Option>
                              <Option value="daytour">一日游</Option>
                              <Option value="inbound">入境游</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item name="isActive" label="状态" valuePropName="checked" initialValue={true}>
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                          </Form.Item>
                        </Col>
                        {category === 'standard' && (
                          <Col span={5}>
                            <Form.Item
                              name="requiresChinaDoc"
                              label="需中国证件"
                              valuePropName="checked"
                              initialValue={false}
                              tooltip="去中国的团：中国护照游客需补传身份证，台湾护照需补传台胞证。机加酒产品请关闭。"
                            >
                              <Switch checkedChildren="需要" unCheckedChildren="不需要" />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                      {categoryHint && (
                        <Alert
                          type="warning"
                          showIcon
                          message={categoryHint}
                          style={{ marginBottom: 16 }}
                        />
                      )}
                      {category === 'daytour' && (
                        <Row gutter={16}>
                          <Col span={4}>
                            <Form.Item name="adultPrice" label="正常价格">
                              <InputNumber min={0} step={0.01} precision={2} prefix="$" style={{ width: '100%' }} placeholder="正常价格" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name="adultCommission" label="原价提成">
                              <InputNumber min={0} step={0.01} precision={2} prefix="$" style={{ width: '100%' }} placeholder="原价提成" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name="seniorPrice" label="优惠价格（55岁+）">
                              <InputNumber min={0} step={0.01} precision={2} prefix="$" style={{ width: '100%' }} placeholder="优惠价格" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name="seniorCommission" label="优惠价提成">
                              <InputNumber min={0} step={0.01} precision={2} prefix="$" style={{ width: '100%' }} placeholder="优惠价提成" />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                      {editingDestination && (
                        <div style={{ background: '#fafbfc', borderRadius: 8, padding: '12px 16px', marginTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: destTours.length > 0 ? 10 : 0 }}>
                            <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}><CalendarOutlined /> 出团日期</span>
                            <DatePicker
                              placeholder="选择日期，可连续添加"
                              style={{ width: 200 }}
                              value={null}
                              onChange={(date) => date && handleQuickAddTour(date)}
                              disabled={addingTour}
                              size="small"
                            />
                            {addingTour && <Spin size="small" />}
                            {!toursLoading && destTours.length === 0 && (
                              <span style={{ color: '#999', fontSize: 13 }}>暂无出团</span>
                            )}
                          </div>
                          {destTours.length > 0 && (
                            <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {destTours.map(t => (
                                <Tag
                                  key={t.id}
                                  color={t.status === 'cancelled' ? 'default' : t.status === 'closed' ? 'orange' : 'blue'}
                                  closable
                                  onClose={(e) => {
                                    e.preventDefault();
                                    handleDeleteTour(t.id, t.productName);
                                  }}
                                >
                                  {t.productName} · {dayjs(t.departureDate).format('MM/DD')}
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )
                };

                const attachmentsTab = {
                  key: 'attachments',
                  label: '附件',
                  children: (
                    <div>
                      <p style={{ color: '#666', marginBottom: 16 }}>选择需要包含的附件（如入境卡填写指南）</p>
                      <Checkbox.Group value={selectedAttachmentIds} onChange={setSelectedAttachmentIds} style={{ width: '100%' }}>
                        <Row gutter={[16, 16]}>
                          {allAttachments.map((att) => (
                            <Col key={att.id} span={12}>
                              <Card size="small" hoverable>
                                <Checkbox value={att.id}>
                                  <Space direction="vertical" size={0}>
                                    <span>{att.name}</span>
                                    {att.category && <Tag color="blue" style={{ marginTop: 4 }}>{att.category}</Tag>}
                                  </Space>
                                </Checkbox>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </div>
                  )
                };

                const standardTabs = [
                  bilingualTab('gathering', '集合与出发说明', 'gatheringInfo', '集合与出发说明（中文）', '集合地点、时间、出发流程等...', 'gatheringInfoEn', 'Gathering & Departure (English)', 'Meeting point, time, departure process, etc...'),
                  bilingualTab('domesticTransport', '境内交通安排', 'domesticTransportInfo', '境内交通安排（中文）', '境内航班、火车、大巴等交通安排...', 'domesticTransportInfoEn', 'Domestic Transport (English)', 'Domestic flights, trains, buses, etc...'),
                  bilingualTab('entry', '入境指南', 'entryGuide', '入境指南（中文）', '入境须知、签证信息等...', 'entryGuideEn', 'Entry Guide (English)', 'Entry requirements, visa information, etc...'),
                  bilingualTab('customs', '海关申报', 'customsInfo', '海关申报说明（中文）', '海关申报注意事项...', 'customsInfoEn', 'Customs Declaration (English)', 'Customs declaration information...'),
                  bilingualTab('vaccine', '疫苗说明', 'vaccineInfo', '疫苗要求（中文）', '如：需接种黄热病疫苗...', 'vaccineInfoEn', 'Vaccine Requirements (English)', 'e.g., Yellow fever vaccine required...'),
                  bilingualTab('local', '注意事项', 'localNotices', '注意事项（中文）', '当地风俗、安全提示等...', 'localNoticesEn', 'Local Notices (English)', 'Local customs, safety tips, etc...'),
                  bilingualTab('insurance', '旅游保险', 'insuranceInfo', '旅游保险说明（中文）', '旅游保险相关说明、保障范围、理赔流程等...', 'insuranceInfoEn', 'Travel Insurance (English)', 'Travel insurance information, coverage, claims process...'),
                  bilingualTab('payment', '支付信息', 'paymentInfo', '支付信息（中文）', '当地货币、兑换方式、支付方式等...', 'paymentInfoEn', 'Payment Information (English)', 'Local currency, exchange, payment methods, etc...'),
                  bilingualTab('weather', '天气信息', 'weatherInfo', '天气信息（中文）', '当地气候、穿衣建议等...', 'weatherInfoEn', 'Weather Information (English)', 'Local climate, clothing suggestions, etc...'),
                ];

                const pickupPointsTab = {
                  key: 'pickupPoints',
                  label: '上车点',
                  children: (
                    <div>
                      <p style={{ color: '#666', marginBottom: 16 }}>上车点为固定地点，请为每个上车点设置上车时间</p>
                      <Form.List name="pickupPoints">
                        {(fields) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                <Form.Item {...restField} name={[name, 'name']} style={{ marginBottom: 0, width: 300 }}>
                                  <Input prefix={<CarOutlined />} readOnly style={{ background: '#f5f5f5' }} />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'time']} style={{ marginBottom: 0, width: 120 }}>
                                  <TimePicker format="HH:mm" placeholder="上车时间" style={{ width: '100%' }} />
                                </Form.Item>
                              </Space>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </div>
                  )
                };

                const daytourTabs = [
                  pickupPointsTab,
                  bilingualTab('schedule', '行程时间参考', 'scheduleRef', '行程时间参考（中文）', '各景点游览时间、行程安排...', 'scheduleRefEn', 'Schedule Reference (English)', 'Visiting time for each attraction, itinerary...'),
                  bilingualTab('meal', '餐食安排', 'mealArrangement', '餐食安排（中文）', '午餐、晚餐安排及说明...', 'mealArrangementEn', 'Meal Arrangement (English)', 'Lunch, dinner arrangement and details...'),
                  bilingualTab('consumption', '小费说明', 'consumptionInfo', '小费说明（中文）', '小费标准、支付方式等...', 'consumptionInfoEn', 'Tipping Info (English)', 'Tipping standards, payment methods, etc...'),
                  bilingualTab('cost', '费用包含与不包含', 'costInclusion', '费用包含与不包含（中文）', '费用包含：...\n费用不包含：...', 'costInclusionEn', 'Cost Inclusion (English)', 'Included: ...\nNot included: ...'),
                  bilingualTab('playNotices', '游玩注意事项', 'playNotices', '游玩注意事项（中文）', '安全提示、着装建议等...', 'playNoticesEn', 'Play Notices (English)', 'Safety tips, dress code suggestions, etc...'),
                  bilingualTab('tips', '重要提示', 'importantTips', '重要提示（中文）', '取消政策、天气影响、特别提醒...', 'importantTipsEn', 'Important Tips (English)', 'Cancellation policy, weather impact, special reminders...'),
                  bilingualTab('purchaseTerms', '购买条款', 'purchaseTerms', '购买条款（中文）', '退改政策、付款方式、责任条款等...', 'purchaseTermsEn', 'Purchase Terms (English)', 'Refund policy, payment methods, liability terms, etc...'),
                ];

                const ttourNames = editingDestination?.ttourNames || [];
                return (
                  <>
                    <div style={{ display: editorMode === 'basic' ? 'block' : 'none' }}>
                      {basicTab.children}
                      <div style={{ background: '#fafbfc', borderRadius: 8, padding: '12px 16px', marginTop: 12 }}>
                        <div style={{ fontWeight: 500, marginBottom: ttourNames.length ? 8 : 0 }}>
                          <SyncOutlined /> TTOUR 团名
                          {ttourNames.some(t => t.differs) && <Tag color="orange" style={{ marginLeft: 8 }}>名称不一致</Tag>}
                          {ttourNames.length === 0 && <span style={{ color: '#999', fontSize: 13, fontWeight: 400, marginLeft: 12 }}>无关联的 TTOUR 未来团期</span>}
                        </div>
                        {ttourNames.length > 0 && (
                          <Space size={[6, 6]} wrap>
                            {ttourNames.map(t => (
                              <Tag key={t.name} color={t.differs ? 'orange' : 'default'} style={{ whiteSpace: 'normal' }}>{t.name}</Tag>
                            ))}
                          </Space>
                        )}
                      </div>
                    </div>
                    <div style={{ display: editorMode === 'notice' ? 'block' : 'none' }}>
                      <Tabs items={category === 'daytour' ? daytourTabs : standardTabs} />
                    </div>
                  </>
                );
              }}
            </Form.Item>
          </Form>
        </div>

        {/* ===== 产品资料区域 ===== */}
        <div style={{ display: editorMode === 'product' ? 'block' : 'none' }}>
          {productLoading ? (
            <Spin style={{ display: 'flex', justifyContent: 'center', padding: 60 }} />
          ) : (
            <>
            {/* 文档上传区 */}
            {!productDoc ? (
              <div style={{ marginBottom: 24 }}>
                <Upload.Dragger
                  customRequest={handleUploadDoc}
                  showUploadList={false}
                  accept=".pdf,.doc,.docx"
                  disabled={docUploading}
                  style={{ padding: '40px 0', background: '#fafafa', borderColor: '#d9d9d9' }}
                >
                  {docUploading ? (
                    <div><Spin size="large" /><p style={{ marginTop: 16, color: '#666' }}>上传中...</p></div>
                  ) : (
                    <div>
                      <p style={{ fontSize: 40, color: '#1890ff', marginBottom: 8 }}><UploadOutlined /></p>
                      <p style={{ fontSize: 16, color: '#333' }}>拖拽产品 PDF 或 Word 到这里</p>
                      <p style={{ color: '#999' }}>或点击选择文件，支持 PDF / Word</p>
                    </div>
                  )}
                </Upload.Dragger>
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <span style={{ color: '#999', marginRight: 8 }}>或者</span>
                  <Button type="link" onClick={() => {
                    // 跳过上传，直接显示编辑表单
                    setProductDoc({ id: 'skip' });
                  }}>手动填写产品资料</Button>
                  <span style={{ color: '#ddd', margin: '0 8px' }}>|</span>
                  <span style={{ color: '#999', marginRight: 4 }}>从其他产品复制：</span>
                  <Select
                    placeholder="选择产品"
                    style={{ width: 300 }}
                    onChange={(v) => { handleCopyProduct(v); setProductDoc({ id: 'skip' }); }}
                    value={null}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    size="small"
                  >
                    {allProducts
                      .filter(p => !productData || p.id !== productData.id)
                      .map(p => (
                        <Option key={p.id} value={p.id}>{p.title || p.destination} {p.duration ? `(${p.duration})` : ''}</Option>
                      ))}
                  </Select>
                </div>
              </div>
            ) : (
              <>
              {/* 已有文档：文档信息栏 */}
              {productDoc.id !== 'skip' && (
                <div style={{ marginBottom: aiExtracting ? 0 : 16 }}>
                  <div style={{ padding: '10px 16px', background: '#f0f5ff', borderRadius: aiExtracting ? '8px 8px 0 0' : 8, border: '1px solid #d6e4ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space>
                      {productDoc.fileType === 'pdf' ? <FilePdfOutlined style={{ fontSize: 20, color: '#ff4d4f' }} /> : <FileWordOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                      <div>
                        <div style={{ fontWeight: 500 }}>{productDoc.fileName}</div>
                        <div style={{ fontSize: 12, color: '#999' }}>{productDoc.fileSize ? (productDoc.fileSize / 1024 / 1024).toFixed(1) + ' MB' : ''}</div>
                      </div>
                    </Space>
                    <Space>
                      <Button
                        type="primary"
                        icon={<RocketOutlined />}
                        onClick={handleAiExtract}
                        loading={aiExtracting}
                      >
                        {aiExtracting ? 'AI 提取中...' : 'AI 提取内容'}
                      </Button>
                      <Button icon={<DownloadOutlined />} size="small" onClick={handleDownloadDoc}>下载</Button>
                      <Upload customRequest={handleUploadDoc} showUploadList={false} accept=".pdf,.doc,.docx">
                        <Button size="small" loading={docUploading}>替换</Button>
                      </Upload>
                      <Popconfirm title="确认删除文档？" onConfirm={() => { handleDeleteDoc(); }}>
                        <Button danger size="small">删除</Button>
                      </Popconfirm>
                    </Space>
                  </div>
                  {aiExtracting && (
                    <div style={{ padding: '8px 16px 12px', background: '#f0f5ff', borderRadius: '0 0 8px 8px', border: '1px solid #d6e4ff', borderTop: 'none', marginBottom: 16 }}>
                      <Progress
                        percent={aiProgress}
                        strokeColor="#1890ff"
                        size="small"
                        format={(p) => {
                          const stage = p < 40 ? '提取产品信息...' : '提取每日行程...';
                          return `${p}% · ${stage}`;
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* 复制产品资料 */}
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f6f8fa', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CopyOutlined style={{ color: '#1890ff' }} />
                <span style={{ color: '#666', whiteSpace: 'nowrap' }}>从其他产品复制资料：</span>
                <Select
                  placeholder="选择产品"
                  style={{ flex: 1 }}
                  onChange={handleCopyProduct}
                  value={null}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {allProducts
                    .filter(p => !productData || p.id !== productData.id)
                    .map(p => (
                      <Option key={p.id} value={p.id}>{p.title || p.destination} {p.duration ? `(${p.duration})` : ''}</Option>
                    ))}
                </Select>
              </div>
            <Form form={productForm} layout="vertical" requiredMark="optional" onValuesChange={() => triggerAutoSave('product')}>
              <Tabs items={[
                {
                  key: 'p-basic',
                  label: '产品信息',
                  children: (
                    <div>
                      <div style={{ borderLeft: '3px solid #1890ff', marginBottom: 16, fontWeight: 500, color: '#1890ff', padding: '6px 12px', fontSize: 13 }}>基本信息</div>
                      <Row gutter={16}>
                        <Col span={3}>
                          <Form.Item label="PDF编号" name="pdfCode">
                            <Input placeholder="C-CSUN06" />
                          </Form.Item>
                        </Col>
                        <Col span={9}>
                          <Form.Item label="产品标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
                            <Input placeholder="如：新西兰南岛绚丽体验6日游" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item label="副标题" name="subtitle">
                            <Input placeholder="简短描述" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item label="行程天数" name="duration">
                            <Input placeholder="6天5晚" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item label="出发地" name="departure">
                            <Input placeholder="基督城" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={10}>
                          <Form.Item label="产品亮点" name="highlights">
                            <Input placeholder="如：冰川湖泊、皇后镇缆车、米佛峡湾、但尼丁苏格兰风情" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="出发时间" name="departureSchedule">
                            <Input placeholder="如：每周日 / 2025年5月起" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="标签" name="tags">
                            <Select mode="tags" placeholder="回车添加" tokenSeparators={[',']} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <div style={{ borderLeft: '3px solid #1890ff', marginBottom: 16, fontWeight: 500, color: '#1890ff', padding: '6px 12px', fontSize: 13 }}>价格信息</div>
                      <Row gutter={16}>
                        <Col span={4}>
                          <Form.Item label="售价" name="price" rules={[{ required: true, message: '请输入' }]}>
                            <InputNumber min={0} prefix="$" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item label="原价" name="originalPrice">
                            <InputNumber min={0} prefix="$" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item label="定金" name="depositAmount">
                            <InputNumber min={0} prefix="$" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item label={<span style={{ fontSize: 12 }}>成人(12+)</span>} name="adultPrice">
                            <Input placeholder="$2399" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item label={<span style={{ fontSize: 12 }}>儿童占床</span>} name="childBedPrice">
                            <Input placeholder="9折" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item label={<span style={{ fontSize: 12 }}>儿童不占床</span>} name="childNoBedPrice">
                            <Input placeholder="65折" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item label={<span style={{ fontSize: 12 }}>婴儿(2岁↓)</span>} name="infantPrice">
                            <Input placeholder="$800起" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item label="其他价格说明" name="pricingDetails">
                        <Input placeholder="如：现付未来旅行优惠下再减$100/人，圣诞/春节无附加费" />
                      </Form.Item>

                      <div style={{ borderLeft: '3px solid #1890ff', marginBottom: 16, fontWeight: 500, color: '#1890ff', padding: '6px 12px', fontSize: 13 }}>产品图片</div>
                      <Row gutter={24}>
                        <Col span={8}>
                          <Form.Item label="封面图" name="coverImage">
                            <ProductImageUpload mode="single" />
                          </Form.Item>
                        </Col>
                        <Col span={16}>
                          <Form.Item label="产品图集" name="images">
                            <ProductImageUpload mode="multiple" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  )
                },
                {
                  key: 'p-cost',
                  label: '费用说明',
                  children: (
                    <div>
                      <Row gutter={24}>
                        <Col span={12}>
                          <Form.Item label="费用包含" name="costInclusion">
                            <RichTextEditor placeholder="• 行程中所列餐食&#10;• 景点项目：行程中所列景点&#10;• 全程中文导游&#10;• 旅游巴士&#10;• 双人间标准酒店住宿..." style={{ minHeight: 200 }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="费用不含" name="costExclusion">
                            <RichTextEditor placeholder="• 奥克兰至基督城的内陆段机票&#10;• 个人消费及小费&#10;• 自费项目费用&#10;• 旅游保险..." style={{ minHeight: 200 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item label="团队用餐" name="mealArrangement">
                        <RichTextEditor placeholder="• 除自理早餐外，早餐均为酒店早餐&#10;• 中式午餐和中式晚餐的标准为八菜一汤&#10;• 特色餐食以行程中描述为准..." style={{ minHeight: 120 }} />
                      </Form.Item>
                      <Form.Item label="自费项目" name="optionalActivities">
                        <RichTextEditor placeholder="自费项目只是列出可供选择的项目，不是指南人有时间完全全部包含。客人根据时间自行选择&#10;&#10;• 米佛峡湾：100NZD/成人，50NZD/儿童&#10;• TSS蒸汽船：100NZD/成人，50NZD/儿童..." style={{ minHeight: 150 }} />
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'p-service',
                  label: '服务安排',
                  children: (
                    <div>
                      <Form.Item label="接送机安排" name="transferInfo">
                        <RichTextEditor placeholder="南岛送机时间和地点：&#10;• 09:30 基督城市中心 24 Hereford St&#10;• 10:00 万国基督城分公司 shop1, Westlink Mall&#10;&#10;南岛送机时间和地点：&#10;• 16:00 基督城内陆机场..." style={{ minHeight: 200 }} />
                      </Form.Item>
                      <Form.Item label="酒店安排" name="hotelPolicy">
                        <RichTextEditor placeholder="公司会根据实际情况安排酒店，但保证级别或以上标准。新西兰酒店入住一般下午2点后，退房一般上午10点前。新西兰的三人间默认为一张大床加一张大床或行军床..." style={{ minHeight: 150 }} />
                      </Form.Item>
                      <Form.Item label="拼房说明" name="roomSharingPolicy">
                        <RichTextEditor placeholder="单人报名产生的单房差，请在报名时向销售人员支付。如果客人想和他人拼房，原则上拼房事宜由客人自行解决..." style={{ minHeight: 120 }} />
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'p-notices',
                  label: '须知与条款',
                  children: (
                    <div>
                      <Form.Item label="特别说明" name="specialNotes">
                        <RichTextEditor placeholder="【禁烟】请所有客人注意，不可在酒店房间内及房间阳台吸烟，违者罚款500纽币起&#10;车上不允许喝不能拧紧瓶盖的饮料等..." style={{ minHeight: 120 }} />
                      </Form.Item>
                      <Form.Item label="退款与更改" name="cancellationPolicy">
                        <RichTextEditor placeholder="出团前15日以外取消订位，收取$800/人；&#10;出团前7-15日取消订位，收取$1600/人；&#10;出团前7日内取消，恕不退款..." style={{ minHeight: 150 }} />
                      </Form.Item>
                      <Form.Item label="条件与限制" name="conditions">
                        <RichTextEditor placeholder="如由于人力不可抗拒的因素导致景点无法参加万国旅行社退还未去景点费用如下：&#10;• 米佛峡湾：100NZD/成人，50NZD/儿童..." style={{ minHeight: 120 }} />
                      </Form.Item>
                      <Form.Item label="保险说明" name="insuranceInfo">
                        <RichTextEditor placeholder="强烈建议客人购买旅游保险！以保障非预期情况下产生的费用。如额外的交通费、取消费、珍贵物品遗失所造成的损失等..." style={{ minHeight: 120 }} />
                      </Form.Item>
                      <Form.Item label="游玩须知" name="playNotices">
                        <RichTextEditor placeholder="其他游玩注意事项..." style={{ minHeight: 100 }} />
                      </Form.Item>
                    </div>
                  )
                }
              ]} />
            </Form>
            </>
            )}
            </>
          )}
        </div>

        {/* ===== 行程管理区域 ===== */}
        <div style={{ display: editorMode === 'itinerary' ? 'block' : 'none' }}>
          <ProductDayEditor destinationId={editingDestination?.id} />
        </div>
      </Drawer>
    </div>
  );
};

export default DestinationManagement;
